import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from './store';
import { workspaceURL } from 'dashboard/helper/URLHelper';

/**
 * Composable for account-related operations.
 * @returns {Object} An object containing account-related properties and methods.
 */
export function useAccount() {
  /**
   * Computed property for the current account ID.
   * @type {import('vue').ComputedRef<number>}
   */
  const route = useRoute();
  const store = useStore();
  const getAccountFn = useMapGetter('accounts/getAccount');
  const activeAccountId = useMapGetter('getCurrentAccountId');
  const getUserAccounts = useMapGetter('getUserAccounts');
  const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const accountId = computed(() => {
    if (route.params.accountId) return Number(route.params.accountId);

    const organizationId = Number(route.params.organizationId);
    const organizationAccountId = getUserAccounts.value?.find(
      account => account.organization_id === organizationId
    )?.id;
    return organizationAccountId || activeAccountId.value;
  });
  const currentAccount = computed(() => {
    const account = getAccountFn.value(accountId.value);
    const userAccount = getUserAccounts.value?.find(
      item => item.id === accountId.value
    );

    return { ...account, ...userAccount };
  });

  /**
   * Generates an account-scoped URL.
   * @param {string} url - The URL to be scoped to the account.
   * @returns {string} The account-scoped URL.
   */
  const accountScopedUrl = url => {
    return workspaceURL(accountId.value, url);
  };

  const isCloudFeatureEnabled = feature => {
    return isFeatureEnabledonAccount.value(currentAccount.value.id, feature);
  };

  const accountScopedRoute = (name, params, query) => {
    const organizationRouteNames = {
      organization_settings_info: 'legacy_organization_settings_info',
      organization_settings_workspaces:
        'legacy_organization_settings_workspaces',
      organization_settings_security: 'legacy_organization_settings_security',
      general_settings_billing: 'legacy_general_settings_billing',
      general_settings_billing_usage: 'legacy_general_settings_billing_usage',
      general_settings_ai_credits: 'legacy_general_settings_ai_credits',
      general_settings_billing_plans: 'legacy_general_settings_billing_plans',
      general_settings_whatsapp_fees: 'legacy_general_settings_whatsapp_fees',
      general_settings_ai_billing: 'legacy_general_settings_ai_billing',
    };
    if (organizationRouteNames[name]) {
      const organizationId = currentAccount.value?.organization_id;
      return {
        name: organizationId ? name : organizationRouteNames[name],
        params: organizationId
          ? { organizationId, ...params }
          : { accountId: accountId.value, ...params },
        query: { ...query },
      };
    }

    return {
      name,
      params: { accountId: accountId.value, ...params },
      query: { ...query },
    };
  };

  const updateAccount = async (data, options) => {
    await store.dispatch('accounts/update', {
      ...data,
      options,
    });
  };

  return {
    accountId,
    route,
    currentAccount,
    accountScopedUrl,
    accountScopedRoute,
    isCloudFeatureEnabled,
    isOnChatwootCloud,
    updateAccount,
  };
}
